/**
 * tasksAdmin.service.ts — Admin > Tasks: the account's task templates (the
 * `tasks` table) and what their form picks from, through the API v5 calls the
 * corporate dashboard's Tasks page uses:
 *   - /v5/tasks (list, detail, create, update; delete = active 0, restore =
 *     update with active 1);
 *   - the type settings' options: header/footers, email and SMS templates,
 *     documents and packets, surveys / e-forms, patient tags, plan stages and
 *     their statuses.
 *
 * What a task does is decided by four columns: autoTaskID (its type),
 * sendToID, contentID and headerFooterID. The automated-task senders skip a
 * row whose columns don't fit its type, without an error anywhere, so
 * `taskActionChecks` holds corp's rules for them. A blank notes / attachments
 * is sent as '' (the API stores the column's empty value) — corp drops blanks,
 * which is why clearing them never saved there. Writes need Admin Access.
 */
import { apiClient } from '@/services/apiClient';
import type { ApiResponse } from '@/types/api.types';

// ─── Task types ────────────────────────────────────────────────────────────────

/** autoTaskID → corp's label. 82 is "Change Plan Stage" to a status (stored, never picked). */
export const TASK_TYPE_LABELS: Record<number, string> = {
  0: 'Human',
  1: 'E-Mail',
  2: 'SMS',
  3: 'Opt-Out Email',
  4: 'Opt-Out SMS',
  5: 'Opt-Out Both',
  6: 'Add Patient Tag',
  7: 'E-mail Survey Link',
  8: 'Change Plan Stage',
  9: 'SMS Survey Link',
  82: 'Change Plan Stage',
};

/** Corp's badge colours per type. */
export const TASK_TYPE_BADGE: Record<number, string> = {
  0: 'bg-slate-100 text-slate-700 border-slate-200',
  1: 'bg-blue-50 text-blue-700 border-blue-200',
  2: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  3: 'bg-orange-50 text-orange-700 border-orange-200',
  4: 'bg-amber-50 text-amber-700 border-amber-200',
  5: 'bg-red-50 text-red-700 border-red-200',
  6: 'bg-violet-50 text-violet-700 border-violet-200',
  7: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  8: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200',
  9: 'bg-teal-50 text-teal-700 border-teal-200',
  82: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200',
};

export const UNKNOWN_TYPE_BADGE = 'bg-slate-100 text-slate-600 border-slate-200';

/** The Task Type picker's order (corp's). */
export const TASK_TYPE_ORDER = [0, 1, 2, 8, 6, 7, 9, 3, 4, 5] as const;

export function taskTypeLabel(autoTaskID: number): string {
  return TASK_TYPE_LABELS[autoTaskID] ?? 'Unknown';
}

/** Anything but Human is sent by the system — and only when assigned to Automatic. */
export function isAutomatedTaskType(autoTaskID: number): boolean {
  return (Number(autoTaskID) || 0) > 0;
}

export interface TaskActionDraft {
  autoTaskID: number;
  sendToID: number;
  /** Template id, or the stage (8) / status (82) / tag (6). */
  contentID: number;
  /** Header/footer id, or the survey for 7 and 9. */
  headerFooterID: number;
  /** Include to require a name too. */
  name?: string;
}

export type TaskActionError = 'name' | 'sendTo' | 'template' | 'stage' | 'tag' | 'survey';

/**
 * Corp's rules for a task's action columns. The senders only act on
 * E-Mail / SMS with a recipient, and every type needs what it sends or sets;
 * Human and the opt-outs need nothing.
 */
export function taskActionChecks(d: TaskActionDraft): { errors: Set<TaskActionError>; messages: string[] } {
  const type = Number(d.autoTaskID) || 0;
  const errors = new Set<TaskActionError>();
  if (d.name !== undefined && d.name.trim() === '') errors.add('name');
  const noContent = !d.contentID;
  if (type === 1 || type === 2) {
    if (!d.sendToID) errors.add('sendTo');
    if (noContent) errors.add('template');
  } else if (type === 8 || type === 82) {
    if (noContent) errors.add('stage');
  } else if (type === 6) {
    if (noContent) errors.add('tag');
  } else if (type === 7 || type === 9) {
    if (!d.headerFooterID) errors.add('survey');
    if (noContent) errors.add('template');
  }
  const messages: string[] = [];
  if (errors.has('name')) messages.push('Enter a task name');
  if (errors.has('sendTo')) messages.push('Select a send-to recipient');
  if (errors.has('template')) messages.push(type === 2 || type === 9 ? 'Select an SMS template' : 'Select an email template');
  if (errors.has('stage')) messages.push('Select a stage');
  if (errors.has('tag')) messages.push('Select a patient tag');
  if (errors.has('survey')) messages.push('Select a survey / e-form');
  return { errors, messages };
}

// ─── Tasks ─────────────────────────────────────────────────────────────────────

export interface AdminTask {
  taskID: number;
  name: string;
  active: boolean;
  autoTaskID: number;
  headerFooterID: number;
  contentID: number;
  sendToID: number;
  /** Comma-separated document ids, packets as P_<id>. */
  attachments: string;
  notes: string;
}

export interface TaskPayload {
  name: string;
  autoTaskID: number;
  headerFooterID: number;
  contentID: number;
  sendToID: number;
  attachments: string;
  notes: string;
}

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function flag(v: unknown): boolean {
  return v === true || v === 1 || v === '1' || v === 'true';
}

function rows(data: unknown): Record<string, unknown>[] {
  return Array.isArray(data) ? (data as Record<string, unknown>[]) : [];
}

export function toAdminTask(row: Record<string, unknown>): AdminTask {
  return {
    taskID: num(row.taskID),
    name: String(row.name ?? ''),
    active: flag(row.active),
    autoTaskID: num(row.autoTaskID),
    headerFooterID: num(row.headerFooterID),
    contentID: num(row.contentID),
    sendToID: num(row.sendToID),
    attachments: String(row.attachments ?? '') === '0' ? '' : String(row.attachments ?? ''),
    notes: String(row.notes ?? ''),
  };
}

/** Every task, deleted ones too. */
export function listAdminTasks(): Promise<AdminTask[]> {
  return apiClient.get<ApiResponse<Record<string, unknown>[]>>('/tasks').then((r) => rows(r.data?.data).map(toAdminTask));
}

export function getAdminTask(id: number): Promise<AdminTask> {
  return apiClient.get<ApiResponse<Record<string, unknown>>>(`/tasks/${id}`).then((r) => toAdminTask(r.data?.data ?? {}));
}

/** Returns the new task's id. */
export function createAdminTask(data: TaskPayload): Promise<number> {
  return apiClient
    .post<ApiResponse<Record<string, unknown>>>('/tasks', { ...data, active: true })
    .then((r) => num(r.data?.data?.taskID));
}

export function updateAdminTask(id: number, data: TaskPayload): Promise<void> {
  return apiClient.put(`/tasks/${id}`, data).then(() => undefined);
}

/** Soft delete: the API sets active 0. */
export function deleteAdminTask(id: number): Promise<void> {
  return apiClient.delete(`/tasks/${id}`).then(() => undefined);
}

export function restoreAdminTask(id: number): Promise<void> {
  return apiClient.put(`/tasks/${id}`, { active: true }).then(() => undefined);
}

// ─── Form options ──────────────────────────────────────────────────────────────

export interface TaskOption {
  id: number;
  label: string;
  active: boolean;
}

export interface DocumentOption {
  /** A document id, or P_<id> for a packet. */
  value: string;
  label: string;
  categoryID: string;
  categoryName: string;
}

/** Admin > Forms/Templates > Header & Footer, in the account's order. */
export function listHeaderFooterOptions(): Promise<TaskOption[]> {
  return apiClient.get<ApiResponse<Record<string, unknown>[]>>('/email-header-footers').then((r) =>
    rows(r.data?.data).map((h) => ({ id: num(h.emailHeaderFooterID), label: String(h.name ?? ''), active: flag(h.active) })),
  );
}

/** Grouped template lists (by category) or flat ones, as "Category → Name". */
function flattenTemplates(data: unknown, idKeys: string[]): TaskOption[] {
  const out: TaskOption[] = [];
  const push = (t: Record<string, unknown>, category: string) => {
    const id = num(idKeys.map((k) => t[k]).find((v) => v != null));
    if (!id) return;
    const name = String(t.name ?? t.title ?? '');
    out.push({ id, label: category ? `${category} → ${name}` : name, active: t.active === undefined ? true : flag(t.active) });
  };
  for (const entry of rows(data)) {
    if (Array.isArray(entry.templates)) {
      const category = String(entry.categoryName ?? entry.name ?? '');
      for (const t of rows(entry.templates)) push(t, category);
    } else {
      const cat = entry.category as Record<string, unknown> | undefined;
      push(entry, String(cat?.name ?? entry.categoryName ?? ''));
    }
  }
  return out;
}

export function listEmailTemplateOptions(): Promise<TaskOption[]> {
  return apiClient
    .get<ApiResponse<unknown>>('/email-templates', { params: { limit: 500, show_inactive: false } })
    .then((r) => flattenTemplates(r.data?.data, ['emailContentID', 'email_content_id', 'id']));
}

export function listSmsTemplateOptions(): Promise<TaskOption[]> {
  return apiClient
    .get<ApiResponse<unknown>>('/sms-templates', { params: { limit: 500, per_page: 500 } })
    .then((r) => flattenTemplates(r.data?.data, ['smsID', 'sms_id', 'id']));
}

export function listDocumentOptions(): Promise<DocumentOption[]> {
  return apiClient.get<ApiResponse<unknown>>('/documents', { params: { per_page: 500 } }).then((r) => {
    const out: DocumentOption[] = [];
    const push = (d: Record<string, unknown>, categoryName: string) => {
      const id = num(d.documentID ?? d.document_id ?? d.id);
      if (!id) return;
      const cat = d.category as Record<string, unknown> | undefined;
      out.push({
        value: String(id),
        label: String(d.title ?? d.name ?? d.fileName ?? id),
        categoryID: String(d.categoryID ?? d.documentCategoryID ?? cat?.documentCategoryID ?? ''),
        categoryName,
      });
    };
    for (const g of rows(r.data?.data)) {
      if (Array.isArray(g.documents)) {
        for (const d of rows(g.documents)) push(d, String(g.categoryName ?? g.title ?? ''));
      } else {
        const cat = g.category as Record<string, unknown> | undefined;
        push(g, String(cat?.name ?? g.categoryName ?? ''));
      }
    }
    return out;
  });
}

export function listDocumentPacketOptions(): Promise<DocumentOption[]> {
  return apiClient.get<ApiResponse<Record<string, unknown>[]>>('/document-packets/templates').then((r) =>
    rows(r.data?.data)
      .map((p) => ({
        value: `P_${num(p.documentPacketId ?? p.documentPacketID ?? p.id)}`,
        label: `${String(p.name ?? '')} (Packet)`,
        categoryID: 'PACKETS',
        categoryName: 'Document Packets',
      }))
      .filter((p) => p.value !== 'P_0'),
  );
}

export function listSurveyOptions(): Promise<TaskOption[]> {
  return apiClient.get<ApiResponse<Record<string, unknown>[]>>('/surveys', { params: { perPage: 1000 } }).then((r) =>
    rows(r.data?.data).map((s) => ({ id: num(s.id), label: String(s.title ?? ''), active: flag(s.isActive) })),
  );
}

export function listTagOptions(): Promise<TaskOption[]> {
  return apiClient.get<ApiResponse<Record<string, unknown>[]>>('/tags-custom', { params: { active: true } }).then((r) =>
    rows(r.data?.data).map((t) => ({ id: num(t.tagsCustomID), label: String(t.title ?? ''), active: true })),
  );
}

export interface StageOption {
  /** What a task stores: the global stage id when the stage has one, else the account stage id. */
  id: number;
  /** The account stage id (what its statuses hang off). */
  accountStageID: number;
  label: string;
}

export function listStageOptions(): Promise<StageOption[]> {
  return apiClient.get<ApiResponse<Record<string, unknown>[]>>('/account-custom-stages').then((r) =>
    rows(r.data?.data).map((s) => {
      const accountStageID = num(s.accountCustomStageID);
      const globalID = num(s.stageID);
      return { id: globalID > 0 ? globalID : accountStageID, accountStageID, label: String(s.stageName ?? '') };
    }),
  );
}

export interface StatusOption {
  id: number;
  label: string;
  /** The account stage it belongs to. */
  accountStageID: number;
}

function toStatus(s: Record<string, unknown>): StatusOption {
  return { id: num(s.stageSubID), label: String(s.name ?? ''), accountStageID: num(s.stageID) };
}

export function listStageStatuses(accountStageID: number): Promise<StatusOption[]> {
  return apiClient
    .get<ApiResponse<Record<string, unknown>[]>>('/stage-subs', { params: { stageID: accountStageID, active: true } })
    .then((r) => rows(r.data?.data).map(toStatus));
}

export function getStageStatus(id: number): Promise<StatusOption | null> {
  return apiClient
    .get<ApiResponse<Record<string, unknown>>>(`/stage-subs/${id}`)
    .then((r) => (r.data?.data ? toStatus(r.data.data) : null))
    .catch(() => null);
}
